import { trim } from 'lodash';
import sanitizeHtml from 'sanitize-html';
import { type Descendant, Text } from 'slate';

import { isGenericPromptInstructionType, type PromptInstructionType } from 'src/data/generation';
import { type ScoringMethod } from 'src/data/scoring';

import { SLATE_TEXT_FORMATS, SLATE_VOID_ELEMENT } from './const';

type HtmlAttributeValue = string | number | boolean | null | undefined;
type HtmlAttributes = Record<string, HtmlAttributeValue>;

export interface SerializeTextOptions {
  skipTextNodeSanitization?: boolean;
  includeLinks?: boolean;
  includeListItemPrefix?: boolean;
}

interface SerializeVariation {
  id?: string;
  score?: number | string;
  isSaved?: boolean;
  imageBlob?: string;
  lastUpdateTime?: string | number;
  assetType?: string;
  generationTool?: string;
  language?: string;
  isScoreLocked?: boolean;
  performancePredictionsScoreLocked?: boolean;
  scoringMethod?: ScoringMethod;
  campaignKeywords?: unknown;
  secondaryScoringMethod?: ScoringMethod;
  benchmarkConfig?: {
    comparisonSet?: { userGoal?: string; name?: string };
    account?: { id?: string; name?: string; channel?: string };
  };
}

interface InstructionData {
  type?: PromptInstructionType;
  instruction?: string;
  id?: string;
}

interface SerializeNode {
  type?: string;
  text?: string;
  children?: SerializeNode[];
  align?: string;
  url?: string;
  src?: string;
  citationId?: string;
  variation?: SerializeVariation;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  id?: string;
  tooltip?: string;
  title?: string;
  info?: string;
  infoTooltip?: string;
  data?: InstructionData;
  bankMessageId?: string;
  bankMessageBody?: string;
  tagName?: string;
  article?: string;
  resourceUrl?: string;
}

const VOID_TAGS = [
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'source',
  'track',
  'wbr',
];

const sanitize = (dirty: string | null | undefined): string =>
  sanitizeHtml(dirty ?? '', {
    allowedTags: [],
    allowedAttributes: {},
  });

const formatStyle = (stylesObj: Record<string, HtmlAttributeValue>) => {
  const styles = Object.entries(stylesObj)
    .filter(([, value]) => value != null && value !== '')
    .map(([name, value]) => `${name}:${value}`);

  if (styles.length === 0) {
    return '';
  }

  return styles.join(';');
};

const formatAttributes = (attributes: HtmlAttributes | null) => {
  if (!attributes) {
    return '';
  }

  const mapped = Object.entries(attributes).map(([key, value]) => `${key}="${value}"`);
  return mapped.join(' ');
};

const formatElement = (tagName: string, attributes: HtmlAttributes | null, children: string) => {
  const attributesStr = formatAttributes(attributes);
  const elementStart = attributesStr.length > 0 ? `${tagName} ${attributesStr}` : tagName;

  if (VOID_TAGS.includes(tagName)) {
    return `<${elementStart} />`;
  } else {
    return `<${elementStart}>${children}</${tagName}>`;
  }
};

const serializeDDVariation = (variation: SerializeVariation, children: string) => {
  const attributes: HtmlAttributes = {
    'data-variation': '',
    'data-variation-score': variation.score,
  };
  if (variation.isSaved) {
    attributes['data-saved-variation-id'] = variation.id;
  } else {
    attributes['data-variation-id'] = variation.id;
  }
  if (variation.imageBlob) {
    attributes['data-image-blob'] = variation.imageBlob;
  }
  if (variation.lastUpdateTime) {
    attributes['data-last-update-time'] = variation.lastUpdateTime;
  }
  if (variation.assetType) {
    attributes['data-asset-type'] = variation.assetType;
  }
  if (variation.generationTool) {
    attributes['data-generation-tool'] = variation.generationTool;
  }
  if (variation.language) {
    attributes['data-language'] = variation.language;
  }
  if (variation.isScoreLocked || variation.performancePredictionsScoreLocked) {
    attributes['data-score-locked'] = '';
  }
  if (variation.scoringMethod) {
    attributes['data-scoring-method'] = variation.scoringMethod.type;
    attributes['data-scoring-method-id'] = variation.scoringMethod.id;
    attributes['data-scoring-method-name'] = encodeURI(sanitize(variation.scoringMethod.name));
  }
  if (variation.campaignKeywords) {
    attributes['data-campaign-keywords'] = encodeURI(
      sanitize(JSON.stringify(variation.campaignKeywords))
    );
  }
  if (variation.secondaryScoringMethod) {
    attributes['data-secondary-scoring-method-name'] = encodeURI(
      sanitize(variation.secondaryScoringMethod.name)
    );
  }
  if (variation.benchmarkConfig) {
    attributes['data-benchmark-config-user-goal'] =
      variation.benchmarkConfig.comparisonSet?.userGoal;
    attributes['data-benchmark-config-account-id'] = variation.benchmarkConfig.account?.id;
    attributes['data-benchmark-config-account-name'] = encodeURIComponent(
      sanitize(variation.benchmarkConfig.account?.name)
    );
    attributes['data-benchmark-config-account-channel'] = encodeURIComponent(
      sanitize(variation.benchmarkConfig.account?.channel)
    );
    attributes['data-benchmark-config-comparison-set-name'] = encodeURIComponent(
      sanitize(variation.benchmarkConfig.comparisonSet?.name)
    );
  }
  return formatElement('div', attributes, children);
};

const serializeBankMessageNode = (node: SerializeNode, children: string) => {
  const attributes: HtmlAttributes = {
    'data-bank-message': true,
    'data-bank-message-id': node.bankMessageId,
    'data-bank-message-body': node.bankMessageBody,
    'data-bank-message-tag-name': node.tagName || '',
  };
  return formatElement('span', attributes, children);
};

const serializeImageNode = (node: SerializeNode, children: string) => {
  const attributes: HtmlAttributes = {
    'data-prompt-image': true,
    'data-prompt-image-id': node.children?.[0]?.id || node.id,
  };
  if (node.tooltip) {
    attributes['data-tooltip'] = node.tooltip;
  }
  return formatElement('span', attributes, children);
};

const serializeAudioNode = (node: SerializeNode, children: string) => {
  const attributes: HtmlAttributes = {
    'data-prompt-audio': true,
    'data-id': node.children?.[0]?.id || node.id,
  };
  if (node.tooltip) {
    attributes['data-tooltip'] = node.tooltip;
  }
  return formatElement('span', attributes, children);
};

const serializeVideoNode = (node: SerializeNode, children: string) => {
  const attributes: HtmlAttributes = {
    'data-prompt-video': true,
    'data-id': node.children?.[0]?.id || node.id,
  };
  if (node.tooltip) {
    attributes['data-tooltip'] = node.tooltip;
  }
  return formatElement('span', attributes, children);
};

export const serializeVariableNode = (node: SerializeNode, children: string) => {
  const attributes: HtmlAttributes = {
    'data-prompt-variable': true,
    'data-prompt-variable-title': node.title || node.children?.[0]?.title,
    'data-prompt-variable-info': node.info || node.children?.[0]?.info,
    'data-prompt-variable-info-tooltip': node.infoTooltip || node.children?.[0]?.infoTooltip,
  };
  return formatElement('span', attributes, children);
};

const serializeInstructionNode = (node: SerializeNode, children: string) => {
  const attributes: HtmlAttributes = {
    'data-prompt-instruction': true,
  };
  if (node.data) {
    attributes['data-instruction-type'] = node.data.type;
    if (node.data.type && isGenericPromptInstructionType(node.data.type)) {
      if (node.data.instruction) {
        attributes['data-instruction'] = node.data.instruction;
      }
    } else {
      attributes['data-id'] = node.data.id;
    }
  }
  return formatElement('span', attributes, children);
};

const serializeDocumentNode = (node: SerializeNode, children: string) => {
  const attributes: HtmlAttributes = {
    'data-prompt-document': true,
    'data-prompt-document-id': node.children?.[0]?.id || node.id,
  };
  if (node.tooltip) {
    attributes['data-tooltip'] = node.tooltip;
  }
  return formatElement('span', attributes, children);
};

const serializeProductDescriptionNode = (node: SerializeNode, children: string) => {
  const attributes: HtmlAttributes = {
    'data-product-description': true,
    'data-product-description-title': node.title,
    'data-product-description-article': node.article,
  };
  return formatElement('span', attributes, children);
};

const serializeResourceUrlNode = (node: SerializeNode, children: string) => {
  const attributes: HtmlAttributes = {
    'data-resource-url': true,
    'data-resource-url-text': node.resourceUrl,
    'data-resource-url-tag-name': node.tagName || '',
  };
  return formatElement('span', attributes, children);
};

const serializeTagNode = (_node: SerializeNode, children: string) => {
  const attributes: HtmlAttributes = {
    'data-prompt-tag': true,
  };
  return formatElement('span', attributes, children);
};

export const serializeTextNode = (
  node: SerializeNode | null | undefined,
  options?: SerializeTextOptions
) => {
  if (!node) {
    return '';
  }
  let string = !options?.skipTextNodeSanitization ? sanitize(node.text) : (node.text ?? '');
  string = string.replace(/\n/g, '<br>');
  if (node.bold) {
    string = `<strong>${string}</strong>`;
  }
  if (node.italic) {
    string = `<em>${string}</em>`;
  }
  if (node.underline) {
    string = `<u>${string}</u>`;
  }
  if (node.strikethrough) {
    string = `<s>${string}</s>`;
  }
  return string;
};

const serializeNodeToHtml = (node: SerializeNode, options?: SerializeTextOptions): string => {
  if (Text.isText(node)) {
    return serializeTextNode(node, options);
  }

  const children =
    node.children?.map((child) => serializeNodeToHtml(child, options)).join('') ?? '';
  const stylesStr = formatStyle({
    'text-align': node.align,
  });
  const attributes = stylesStr.length > 0 ? { style: stylesStr } : null;
  const variation = node.variation || {};

  switch (node.type) {
    case SLATE_TEXT_FORMATS.HEADING_ONE:
      return formatElement('h1', attributes, children);
    case SLATE_TEXT_FORMATS.HEADING_TWO:
      return formatElement('h2', attributes, children);
    case SLATE_TEXT_FORMATS.HEADING_THREE:
      return formatElement('h3', attributes, children);
    case SLATE_TEXT_FORMATS.HEADING_FOUR:
      return formatElement('h4', attributes, children);
    case SLATE_TEXT_FORMATS.HEADING_FIVE:
      return formatElement('h5', attributes, children);
    case SLATE_TEXT_FORMATS.HEADING_SIX:
      return formatElement('h6', attributes, children);

    case SLATE_TEXT_FORMATS.BULLETED_LIST:
      return formatElement('ul', attributes, children);
    case SLATE_TEXT_FORMATS.NUMBERED_LIST:
      return formatElement('ol', attributes, children);
    case SLATE_TEXT_FORMATS.LIST_ITEM:
      return formatElement('li', attributes, children);

    case SLATE_TEXT_FORMATS.BLOCK_QUOTE:
      return formatElement('blockquote', attributes, children);

    case SLATE_TEXT_FORMATS.PARAGRAPH:
      return formatElement('p', attributes, children);

    case SLATE_VOID_ELEMENT.DIVIDER: {
      const localAttributes: HtmlAttributes = {
        ...attributes,
        'data-divider': true,
      };
      return formatElement('div', localAttributes, children);
    }

    case SLATE_TEXT_FORMATS.LINK: {
      const localAttributes: HtmlAttributes = {
        ...attributes,
        href: sanitize(node.url),
      };
      if (node?.citationId) {
        localAttributes['data-citation-id'] = node.citationId;
      }

      return formatElement('a', localAttributes, children);
    }

    case SLATE_TEXT_FORMATS.IMG: {
      const localAttributes: HtmlAttributes = {
        ...attributes,
        src: sanitize(node.src),
      };

      return formatElement('img', localAttributes, children);
    }

    case SLATE_TEXT_FORMATS.DD_VARIATION:
      return serializeDDVariation(variation, children);

    case SLATE_TEXT_FORMATS.BANK_MESSAGE:
      return serializeBankMessageNode(node, children);

    case SLATE_TEXT_FORMATS.PRODUCT_DESCRIPTION:
      return serializeProductDescriptionNode(node, children);

    case SLATE_TEXT_FORMATS.RESOURCE_URL:
      return serializeResourceUrlNode(node, children);

    case SLATE_TEXT_FORMATS.PROMPT_IMAGE:
      return serializeImageNode(node, children);

    case SLATE_TEXT_FORMATS.PROMPT_DOCUMENT:
      return serializeDocumentNode(node, children);

    case SLATE_TEXT_FORMATS.PROMPT_TAG:
      return serializeTagNode(node, children);

    case SLATE_TEXT_FORMATS.EXTERNAL_CHANNEL_TEXT:
      return formatElement('span', attributes, children);

    case SLATE_TEXT_FORMATS.PROMPT_VARIABLE:
      return serializeVariableNode(node, children);

    case SLATE_TEXT_FORMATS.PROMPT_INSTRUCTION:
      return serializeInstructionNode(node, children);

    case SLATE_TEXT_FORMATS.PROMPT_AUDIO:
      return serializeAudioNode(node, children);

    case SLATE_TEXT_FORMATS.PROMPT_VIDEO:
      return serializeVideoNode(node, children);

    default:
      return children;
  }
};

/**
 * Serializes Slate nodes to HTML
 * @param nodes - Slate nodes
 * @param [options] - Options
 * @returns - HTML text
 */
export const slateToHtml = (
  nodes: Descendant[] | null | undefined,
  options?: SerializeTextOptions
) => {
  return nodes?.map((node) => serializeNodeToHtml(node, options)).join('') ?? '';
};

/**
 * Serializes Slate nodes to text
 * @param nodes - Slate nodes
 * @param [options] - Options
 * @returns - Text
 */
export const slateToText = (
  nodes: Descendant[] | null | undefined,
  options?: SerializeTextOptions
) => {
  if (!nodes) {
    return '';
  }
  return nodes.map((node, index) => serializeNodeToText(node, { index }, options)).join('\n');
};

interface SerializeTextParams {
  index: number;
  parent?: SerializeNode;
}

const serializeNodeToText = (
  node: SerializeNode,
  params: SerializeTextParams,
  options?: SerializeTextOptions
): string => {
  if (Text.isText(node)) {
    return node.text;
  }

  const childrenLines = (node.children ?? []).map((childNode, childIndex) =>
    serializeNodeToText(childNode, { index: childIndex, parent: node }, options)
  );

  switch (node.type) {
    case SLATE_TEXT_FORMATS.BULLETED_LIST:
    case SLATE_TEXT_FORMATS.NUMBERED_LIST: {
      return childrenLines.join('\n');
    }

    case SLATE_TEXT_FORMATS.LIST_ITEM: {
      const includeListItemPrefix = options?.includeListItemPrefix || false;
      const isNumberedList = params?.parent?.type === SLATE_TEXT_FORMATS.NUMBERED_LIST;
      const prefix = includeListItemPrefix ? (isNumberedList ? `${params.index + 1}. ` : '- ') : '';
      const content = childrenLines.join('');
      return `${prefix}${content}`;
    }

    case SLATE_TEXT_FORMATS.LINK: {
      const includeLinks = options?.includeLinks || false;
      const content = childrenLines.join('');
      return includeLinks && trim(node.url) ? `[${content}](${node.url})` : content;
    }

    case SLATE_TEXT_FORMATS.HEADING_ONE:
    case SLATE_TEXT_FORMATS.HEADING_TWO:
    case SLATE_TEXT_FORMATS.HEADING_THREE:
    case SLATE_TEXT_FORMATS.HEADING_FOUR:
    case SLATE_TEXT_FORMATS.HEADING_FIVE:
    case SLATE_TEXT_FORMATS.HEADING_SIX:
    case SLATE_TEXT_FORMATS.BLOCK_QUOTE:
    case SLATE_TEXT_FORMATS.PARAGRAPH:
    default: {
      return childrenLines.join('');
    }
  }
};
